import React from 'react';

import { Box } from '@rbilabs/universal-components';
import { Redirect } from 'expo-router';
import { useIntl } from 'react-intl';

import { CardModal } from 'components/card-modal';
import LoadingAnimation from 'components/loading-animation';
import useRedirectAuthIfBrazeId from 'components/phone-from-braze-id/use-redirect-auth-if-braze-id';
import { KeyboardAwareView } from 'components/ucl/keyboard-aware-view';
import useUnauthRedirects from 'hooks/auth/use-unauth-redirects';
import { useRoute } from 'hooks/navigation/use-route';
import useEffectOnce from 'hooks/use-effect-once';
import { useFeatureAccountForm } from 'hooks/use-feature-account-form';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import CampaignHeadline from 'pages/authentication/components/auth-campaigns/campaign-headline';
import useAuthCampaign from 'pages/authentication/components/auth-campaigns/hooks/use-auth-campaign';
import useAuthFlow from 'state/auth/hooks/use-auth-flow';
import { IAuthRouteParams } from 'state/auth/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useScrollContext } from 'state/scroll';
import { useScrollViewFullHeight } from 'state/scroll/hooks';
import { EventName, emitEvent } from 'utils/event-hub';
import { routes } from 'utils/routing';

import { AUTH_CAMPAIGN_PARAM_ID } from '../components/auth-campaigns/constants';
import SignUpHeadline from '../components/sign-up-headline/sign-up-headline';

import SignInTab from './sign-in-tab';
import { AuthContainer, SignUpContainer, SignUpHomeContainer } from './sign-up-home.styled';
import SignUpTab from './sign-up-tab';

const SignupHome = () => {
  useScrollViewFullHeight();
  useRedirectAuthIfBrazeId();
  const isMobile = useIsMobileBreakpoint();
  const { formatMessage } = useIntl();
  const { scrollRef } = useScrollContext();
  const { params } = useRoute<IAuthRouteParams & { [AUTH_CAMPAIGN_PARAM_ID]?: string }>();
  const { featureAccountForm, featureAccountFormLoading } = useFeatureAccountForm();
  const campaignIdParam = params[AUTH_CAMPAIGN_PARAM_ID];
  const { attemptedSignUpWithExistingEmail } = params;

  const campaignContent = featureAccountForm?.activeCampaigns?.find(
    campaign => campaign?.campaignId === campaignIdParam
  );
  const { extraFields } = useAuthCampaign({
    campaignId: campaignIdParam,
    campaignContent,
  });

  const { activeRouteIsSignIn, showOtpForm } = useAuthFlow();

  // TODO React Navigation: we may need to update this to a useEffect
  // if RN Nav isn't update on each load
  useEffectOnce(() => {
    emitEvent(EventName.SCREEN_RENDER, {
      screenId: 'signupHome',
    });
  });

  const showSignUpTab = params.email && !showOtpForm;

  const component = showSignUpTab ? (
    <SignUpTab showOtpForm={showOtpForm} extraFields={extraFields} />
  ) : (
    <SignInTab
      showOtpForm={showOtpForm}
      extraFields={extraFields}
      attemptedSignUpWithExistingEmail={attemptedSignUpWithExistingEmail}
    />
  );

  const enableSignUpFlag = useFlag(LaunchDarklyFlag.ENABLE_SIGN_UP);

  if (enableSignUpFlag === false) {
    <Redirect href={routes.base} />;
  }

  const redirect = useUnauthRedirects();
  if (redirect) {
    return redirect;
  }

  return (
    <CardModal
      testID="signup-home-container"
      helmetTitle={formatMessage({ id: 'signUp' })}
      alwaysShowHeader
    >
      <KeyboardAwareView
        withScroll
        ref={ref => (scrollRef.current = ref)}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <SignUpHomeContainer $backgroundColor={campaignContent?.campaingBackgroundColor?.hex}>
          <AuthContainer>
            <Box justifyContent="center" alignItems="center" height={isMobile ? '$40' : '$64'}>
              {campaignIdParam && featureAccountFormLoading ? (
                <LoadingAnimation />
              ) : campaignContent ? (
                <CampaignHeadline campaign={campaignContent} />
              ) : (
                <SignUpHeadline />
              )}
            </Box>
            <SignUpContainer isSignIn={activeRouteIsSignIn}>{component}</SignUpContainer>
          </AuthContainer>
        </SignUpHomeContainer>
      </KeyboardAwareView>
    </CardModal>
  );
};

export default React.memo(SignupHome);
